/* eslint-disable no-cond-assign */
/* eslint-disable consistent-return */

// Third Party
import chroma from 'chroma-js';

// Components
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';

// Utils
import colorUtils from '../../../../utils/colors';
import { getCorrelationLabelDisplayedText } from '../../../../utils/correlation-matrix';

// Types
import { formatLocalizedString, formatMessage } from '../../../../utils/i18n';
import { CorrelationMatrixDataCell, CorrelationMatrixLabelCell, NotApplicableCell, ProposalItemLabelCell } from '../../../../views/components/cells';
import ProposalTableData from '@/types/proposal-table-data';
import ProposalTableRowData from '@/types/proposal-table-row-data';
import ProposalTableCellData from '@/types/proposal-table-cell-data';
import RiskPeriods from '@/types/risk-periods';
import ComparisonHolding from '@/types/comparison-holding';
import CorrelationData from '@/types/correlation-data';
import { monthsToPeriod } from '@/utils/time';

const DARK_TEXT_COLOR = '#5a6375';
const LIGHT_TEXT_COLOR = '#ffffff';
const colorScale = chroma.scale(['#c01b74', '#ece9f8', '#0a8adb']);

export default class CorrelationMatrixDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new CorrelationMatrixDAO(proposal, proposalElement);
  }

  data: ProposalTableData = { rows: [] };

  period: RiskPeriods = 'INC';

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    // Note on shortHistory VS useLongestHistory: shortHistory is a legacy term that is used in the proposal
    // but it really means that we want to use the longest history available for the item (settings in the comparison/proposal)
    // shortHistory was probably named this way to indicate that we would allow a holding of a portfolio to have shorter history than the rest
    const { shortHistory: useLongestHistory, commonInception, showBaseCurrencies, readOnly } = this.proposal;
    const toolbarOption = this.getToolbarOption();

    let period: RiskPeriods = '3Y';

    if (toolbarOption !== undefined && toolbarOption.monthsRange !== undefined) {
      period = monthsToPeriod(toolbarOption.monthsRange) as RiskPeriods;
    }
    this.period = period;

    const items: ComparisonItemDAO[] = this.getItemDAOs();
    let rows: ProposalTableRowData[] = [];
    const header: ProposalTableRowData = { data: [] };

    const item = items[0];
    if (!item) {
      this.data = { rows };
      return;
    }

    header.data.push({
      value: formatLocalizedString(item.item.name, this.locale),
      cellType: ProposalItemLabelCell,
      color: item.item.color,
      itemType: item.item.type,
      currency: showBaseCurrencies === true ? item.item.currency : undefined,
    });

    const holdings: DataPointResponse<ComparisonHolding[]> = DataPoints.getItemHoldings(item.item);
    const correlation = DataPoints.getCorrelationMatrixByPeriod(item.item, this.period, !!readOnly, {
      useLongestHistory,
      commonInception,
      useBaseCurrency: showBaseCurrencies,
    });

    if (!correlation || !correlation.response || correlation.response.length === 0) {
      this.data = this.getDataWhenNoCorrelationToDisplay(header);
      return;
    }

    this.addDisclaimerKeys(correlation.disclaimerKeys, item.item);

    // Filter duplicates
    const uniqueCorrelation = correlation.response.filter(c => c.holdings[0] !== c.holdings[1]);
    const uniqueHoldings = holdings.response.filter((holdingItem, i, arr) => arr.map(value => value.id).indexOf(holdingItem.id) === i);

    if (uniqueHoldings.length !== 0) {
      this.addDisclaimerKeys(holdings.disclaimerKeys, item.item);
    }

    // Add Data Rows
    for (let i = 0; i < uniqueHoldings.length - 1; i++) {
      const rowData = [];

      for (let j = uniqueHoldings.length - 1; j > i; j--) {
        const correlationPoint = uniqueCorrelation.find(
          (c: CorrelationData) => c.holdings.includes(uniqueHoldings[i].id) && c.holdings.includes(uniqueHoldings[j].id),
        );

        if (correlationPoint) {
          rowData.push(correlationPoint);
        } else {
          rowData.push({
            holdings: [uniqueHoldings[i].id, uniqueHoldings[j].id],
            value: null,
          });
        }
      }

      const columns = this.getColumns(rowData, uniqueHoldings);

      const labelCell = {
        className: 'correlation-y-label',
        value: getCorrelationLabelDisplayedText(uniqueHoldings[i]),
        cellType: CorrelationMatrixLabelCell,
        tooltipMessage: uniqueHoldings[i] && uniqueHoldings[i].name ? formatLocalizedString(uniqueHoldings[i].name) : undefined,
      };

      rows.push({
        data: [labelCell, ...columns],
      });
    }

    rows = rows.reverse();

    // Add Footer Row
    const emptyCell = {
      value: '',
      cellType: CorrelationMatrixLabelCell,
    };

    const footerColumns = this.getFooterColumns(uniqueHoldings);

    rows.push({
      data: [emptyCell, ...footerColumns],
    });

    let colspan = 1;
    for (const row of rows) {
      const cells = row.data.length;
      if (cells > colspan) {
        colspan = cells;
      }
    }

    header.data[0].colSpan = colspan;

    this.data = {
      header,
      rows,
    };
  }

  /* eslint-disable-next-line class-methods-use-this */
  getTableClassName(): string {
    return 'correlation-matrix-table';
  }

  /* eslint-disable-next-line class-methods-use-this */
  getTableData(/* skipRows?: number, limitRows?: number */): ProposalTableData {
    return this.data;
  }

  private getDataWhenNoCorrelationToDisplay = (header: ProposalTableRowData) => ({
    header,
    rows: [
      {
        data: [
          {
            value: '',
            cellType: NotApplicableCell,
          },
        ],
      },
    ],
  });

  private getColumns = (rowData: CorrelationData[], holdings: ComparisonHolding[]): ProposalTableCellData[] =>
    rowData.map(datum => {
      if (datum.value != null) {
        const { value } = datum;

        // Force values into a [0,1] range to cover full scale.
        const backgroundColor = colorScale((value + 1) * 0.5).toString();
        const color = colorUtils.isLight(backgroundColor, 0.6) ? DARK_TEXT_COLOR : LIGHT_TEXT_COLOR;

        // Find holding symbols for data point
        const symbols = holdings.filter(h => datum.holdings.indexOf(h.id) > -1).map(holding => holding.symbol || formatLocalizedString(holding.name));

        // Remove the integer part (or the first zero if value is 1 or -1): 0.12 -> .12 / 1.00 -> 1.0
        // IMPORTANT: Only use this function for values between -1 and 1, inclusively
        const shorten = (val: number) => val.toFixed(2).replace('0', '');

        const tooltipMessage = formatMessage('Charts.Correlation.ValueTooltip', {
          value: shorten(value),
          item1: symbols[0] || '',
          item2: symbols[1] || '',
        });

        return {
          className: 'value',
          value: shorten(value),
          cellType: CorrelationMatrixDataCell,
          color,
          backgroundColor,
          tooltipMessage,
        };
      }

      return {
        value: null,
        cellType: CorrelationMatrixDataCell,
      };
    });

  private getFooterColumns = (holdings: ComparisonHolding[]) =>
    holdings
      .slice(1)
      .reverse()
      .map(holding => ({
        className: 'correlation-x-label',
        value: getCorrelationLabelDisplayedText(holding),
        cellType: CorrelationMatrixLabelCell,
        tooltipMessage: holding.name ? formatLocalizedString(holding.name) : undefined,
      }));
}
